import React, { FC, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import "moment/locale/fr";    // French (default)
import "moment/locale/de";    // German (default)
import "moment/locale/es";    // Spanish (default)
import "moment/locale/it";    // Italian (default)
import "moment/locale/ja";    // Japanese
import "moment/locale/ko";    // Korean
import "moment/locale/zh-cn"; // Chinese (Simplified)
import "moment/locale/zh-tw"; // Chinese (Traditional)
import "moment/locale/ru";    // Russian
import "moment/locale/ar";    // Arabic
import "moment/locale/id";    // Indonesian
import useAccessToken from '../../../hooks/AccessToken'
import {
  InvoicePages,
  InvoiceMultiPageSetting,
} from '../../../interfaces/Invoices'
import { Modal } from 'react-bootstrap'
import { getInvoiceDetail } from '../../Invoices/InvoiceUtils'
import { InvoicePreview } from '../../Invoices/PreviewInvoice'
import { LoadingInvoiceContent } from '../../Invoices/InvoiceComponentParts'
import { Pipeline } from '../../../interfaces'
import { RecentChatHistoryPipeline } from '../../../interfaces/Chat/RecentChatHistory'
import { useIntl } from 'react-intl'
export type InvoiceRenderContext = 'preview' | 'send-whatsapp' | 'resend-whatsapp' // 'download'
export type InvoiceRouterContext = 'add-log' | 'log' | 'list-invoice'

interface InvoiceAttachmentsProps {
  context: InvoiceRenderContext
  routerContext: InvoiceRouterContext
  lead: Pipeline | RecentChatHistoryPipeline
  invoice: InvoiceMultiPageSetting
  showInvoiceModal?: boolean
  handleInvoiceModalClose?: (value: boolean) => void
  closeModalRef?: React.RefObject<HTMLButtonElement>
  typeInvoiceAttachments?: 'default' | 'web-view-preview'
  tokenWebView?: string
}

export const InvoiceAttachments: FC<InvoiceAttachmentsProps> = ({
  context,
  routerContext,
  lead,
  invoice,
  showInvoiceModal,
  handleInvoiceModalClose,
  closeModalRef,
  typeInvoiceAttachments = 'default',
  tokenWebView,
}) => {
  const token = useAccessToken()
  const [multiPages, setMultiPages] = useState<InvoicePages[] | null>(null) // TODO: MAIN
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [stepForm, setStepForm] = useState<number>(0)  // TODO: (UNUSED)
  const [column, setColumn] = useState<any[]>([])
  const intl = useIntl()

  const formSchema = Yup.object().shape({
    id: Yup.string().required('ID is required'),
    name: Yup.string().required(
      intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Nama' })
    ),
    caption: Yup.string(),
    progressId: Yup.string()
      .uuid('Invalid progress id')
      .nullable() // Memungkinkan nilai null
      .required(intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Progress' })),
    leadTemplateId: Yup.string().required(
      intl.formatMessage({ id: 'FORM.VALIDATION.CHOOSE' }, { title: 'Pipe' })
    ),
    format: Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      id: '',
      name: '',
      caption: '',
      progressId: '',
      leadTemplateId: 'd2d41893-ecf5-4322-bc8d-35282f22705f',
      format: 'pdf',
      backgroundImage: [],
    },
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: formSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (stepForm === 0) {
        return void 0
      }
    },
  })

  useEffect(() => {
    if (invoice) {
      setIsLoading(true)
      if (typeInvoiceAttachments === 'default') {
        getInvoiceDetail('preview', invoice, token, formik, column, setMultiPages, setIsLoading)
      } else {
        getInvoiceDetail('preview', invoice, tokenWebView!, formik, column, setMultiPages, setIsLoading)
      }
    }
  }, [invoice, column])

  return (
    <div>
      {(context === 'preview' || context === 'resend-whatsapp') && (routerContext === 'log' || routerContext === 'list-invoice') ? (
        <Modal
          ref={modalRef}
          show={showInvoiceModal}
          onHide={() =>
            handleInvoiceModalClose && handleInvoiceModalClose(false)
          }
          centered
          backdrop='static'
          keyboard={false}
          size='xl'
        >
          <Modal.Header
            closeButton
          >
            <Modal.Title>{intl.formatMessage({ id: 'INVOICE.PANEL.LOG_PREVIEW_PAGES' })}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '0px', minHeight: '400px' }}>
            {isLoading ? (
              <LoadingInvoiceContent />
            ) : (
              <ShowInvoicePreview
                routerContext={routerContext}
                context={context}
                lead={lead}
                invoice={invoice}
                isLoading={isLoading}
                formik={formik}
                multiPages={multiPages}
                stepForm={stepForm}
                column={column}
                extractPages={false}
                setMultiPages={setMultiPages}
                handleInvoiceModalClose={handleInvoiceModalClose}
                closeModalRef={closeModalRef}
                typeInvoiceAttachments={typeInvoiceAttachments}
              />
            )}
          </Modal.Body>
        </Modal>
      ) : context === 'send-whatsapp' && routerContext === 'add-log' ? (
        <>
          {isLoading ? (
            <LoadingInvoiceContent />
          ) : (
            <ShowInvoicePreview
              routerContext={routerContext}
              context={context}
              lead={lead}
              invoice={invoice}
              isLoading={isLoading}
              formik={formik}
              multiPages={multiPages}
              stepForm={stepForm}
              column={column}
              extractPages={false}
              setMultiPages={setMultiPages}
              handleInvoiceModalClose={handleInvoiceModalClose}
              closeModalRef={closeModalRef}
              typeInvoiceAttachments={typeInvoiceAttachments}
              tokenWebView={tokenWebView}
            />
          )}
        </>
      ) : (
        <LoadingInvoiceContent />
      )}
    </div>
  )
}

const ShowInvoicePreview = ({
  routerContext,
  context,
  isLoading,
  formik,
  multiPages,
  stepForm,
  column,
  extractPages,
  setMultiPages,
  invoice,
  lead,
  handleInvoiceModalClose,
  closeModalRef,
  typeInvoiceAttachments,
  tokenWebView
}: {
  routerContext: InvoiceRouterContext
  context: InvoiceRenderContext
  isLoading: boolean
  formik: any
  multiPages: InvoicePages[] | null
  stepForm: number
  column: any[]
  extractPages: boolean
  setMultiPages: React.Dispatch<React.SetStateAction<InvoicePages[] | null>>
  invoice: InvoiceMultiPageSetting
  lead: Pipeline | RecentChatHistoryPipeline
  handleInvoiceModalClose?: (value: boolean) => void
  closeModalRef?: React.RefObject<HTMLButtonElement>
  typeInvoiceAttachments?: 'default' | 'web-view-preview'
  tokenWebView?: string
}) => {
  return (
    <div
      className='stepper stepper-links d-flex flex-column first m-0'
      id='kt_create_account_stepper'
      data-kt-stepper='true'
    >
      {isLoading ? (
        <div style={{ height: '500px' }} className='d-flex align-items-center justify-content-center'>
          Loading...
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit} noValidate>
          {multiPages && multiPages.length > 0 ? (
            <>
              <div
                className={`${stepForm === 0 ? 'current' : 'pending'} `}
                data-kt-stepper-element='content'
              >
                <InvoicePreview
                  routerContext={typeInvoiceAttachments === 'default' ? 'pipeline' : 'web-view-preview'}
                  invoice={invoice}
                  column={column}
                  multiPages={multiPages}
                  extractPages={extractPages}
                  setMultiPages={setMultiPages}
                  format={'pdf'}
                  showDownloadBtn={true}
                  showFormButton={(context === 'send-whatsapp' || context === 'resend-whatsapp') ? true : false}
                  handleInvoiceModalClose={handleInvoiceModalClose}
                  closeModalRef={closeModalRef}
                  tokenWebView={tokenWebView}
                />
              </div>
            </>
          ) :
            <div style={{ height: '500px' }} className='d-flex align-items-center justify-content-center'>
              Loading Data...
            </div>}
        </form>
      )}
    </div>
  )
}
